import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, del } from '@/lib/api'

// ==================== Types ====================

export interface CurrencyTransfer {
    id: string
    fromAccountId: string
    toAccountId: string
    fromAmount: number
    toAmount: number
    fromCurrency: string
    toCurrency: string
    exchangeRate: number
    fee: number
    transferDate: string
    description: string | null
    createdBy: string
    createdAt: string
}

export interface TransferWithAccounts extends CurrencyTransfer {
    fromAccount: { id: string; name: string; currency: string } | null
    toAccount: { id: string; name: string; currency: string } | null
}

export interface CreateTransferInput {
    fromAccountId: string
    toAccountId: string
    fromAmount: number
    toAmount: number
    exchangeRate: number
    fee?: number
    transferDate: string
    description?: string
}

export interface TransferListParams {
    page?: number
    pageSize?: number
    fromAccountId?: string
    toAccountId?: string
    startDate?: string
    endDate?: string
}

interface TransferListResponse {
    data: TransferWithAccounts[]
    meta: { page: number; pageSize: number; total: number; totalPages: number }
}

export interface ExchangeRateHistory {
    exchangeRate: number
    transferDate: string
    fromAmount: number
    toAmount: number
    createdAt: string
}

export interface LatestRate {
    fromCurrency: string
    toCurrency: string
    exchangeRate: number
    transferDate: string
}

export interface RateHistoryResponse {
    fromCurrency: string
    toCurrency: string
    latestRate: number | null
    history: ExchangeRateHistory[]
}

// ==================== Query Keys ====================

export const transferKeys = {
    all: ['transfers'] as const,
    lists: () => [...transferKeys.all, 'list'] as const,
    list: (params?: TransferListParams) => [...transferKeys.lists(), params] as const,
    rates: () => [...transferKeys.all, 'rates'] as const,
    rateHistory: (from: string, to: string) => [...transferKeys.rates(), from, to] as const,
}

// ==================== Hooks ====================

/**
 * 获取换汇记录列表
 */
export function useTransfers(params: TransferListParams = {}) {
    return useQuery({
        queryKey: transferKeys.list(params),
        queryFn: async () => {
            const searchParams = new URLSearchParams()
            if (params.page) {searchParams.set('page', params.page.toString())}
            if (params.pageSize) {searchParams.set('pageSize', params.pageSize.toString())}
            if (params.fromAccountId) {searchParams.set('fromAccountId', params.fromAccountId)}
            if (params.toAccountId) {searchParams.set('toAccountId', params.toAccountId)}
            if (params.startDate) {searchParams.set('startDate', params.startDate)}
            if (params.endDate) {searchParams.set('endDate', params.endDate)}

            const query = searchParams.toString()
            return get<TransferListResponse>(`/finance/transfers${query ? `?${query}` : ''}`)
        },
    })
}

/**
 * 创建换汇记录
 */
export function useCreateTransfer() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: CreateTransferInput) => post<CurrencyTransfer>('/finance/transfers', data),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: transferKeys.all })
            // 同时刷新账户和现金流，因为换汇会影响余额
            void queryClient.invalidateQueries({ queryKey: ['accounts'] })
            void queryClient.invalidateQueries({ queryKey: ['cashFlows'] })
        },
    })
}

/**
 * 删除换汇记录
 */
export function useDeleteTransfer() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => del(`/finance/transfers/${id}`),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: transferKeys.all })
            void queryClient.invalidateQueries({ queryKey: ['accounts'] })
            void queryClient.invalidateQueries({ queryKey: ['cashFlows'] })
        },
    })
}

/**
 * 获取所有币种对的最新汇率
 */
export function useAllLatestRates() {
    return useQuery({
        queryKey: transferKeys.rates(),
        queryFn: () => get<LatestRate[]>('/finance/transfers/rates'),
        staleTime: 1000 * 60 * 5, // 5 分钟缓存
    })
}

/**
 * 获取指定币种对的汇率历史
 * @param fromCurrency 源币种
 * @param toCurrency 目标币种
 * @param limit 返回数量限制
 */
export function useExchangeRateHistory(
    fromCurrency: string,
    toCurrency: string,
    limit = 10
) {
    return useQuery({
        queryKey: transferKeys.rateHistory(fromCurrency, toCurrency),
        queryFn: () =>
            get<RateHistoryResponse>(
                `/finance/transfers/rates/${fromCurrency}/${toCurrency}?limit=${limit}`
            ),
        enabled: !!fromCurrency && !!toCurrency,
        staleTime: 1000 * 60 * 5, // 5 分钟缓存
    })
}
